'use client';

/**
 * Logout Button Component
 *
 * Provides sign-out functionality for authenticated users.
 * Invalidates the session and redirects to login page.
 *
 * [Source: docs/stories/4-1-implement-better-auth-authentication.md#AC-2]
 * [Source: docs/stories/4-1-implement-better-auth-authentication.md#Task-3.5]
 */
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { signOut } from '@pagelane/auth/client';
import { toast } from 'sonner';
import { LogOut } from 'lucide-react';
import { Button, type ButtonProps } from '@/components/ui/button';

interface LogoutButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** Show icon only (no text) */
  iconOnly?: boolean;
}

export function LogoutButton({
  iconOnly = false,
  variant = 'ghost',
  size = iconOnly ? 'icon' : 'default',
  ...props
}: LogoutButtonProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  async function handleLogout() {
    setIsLoading(true);

    try {
      await signOut();
      toast.success('Signed out successfully');
      router.push('/login');
      router.refresh();
    } catch (error) {
      toast.error('Failed to sign out. Please try again.');
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleLogout}
      disabled={isLoading}
      {...props}
    >
      <LogOut className="h-4 w-4" />
      {!iconOnly && <span>{isLoading ? 'Signing out...' : 'Sign out'}</span>}
    </Button>
  );
}
